// CreateCallDialog.js - OPTIMIZED: Fixed constant re-rendering with memoized components

import React, { useState, useMemo, useEffect, useCallback, memo } from 'react';
import {
  Box, Button, Checkbox, CircularProgress, Dialog, DialogActions,
  DialogContent, ListItemText, MenuItem, Slider, TextField,
  Typography, Paper, Stack, Chip, Divider, FormHelperText,
  IconButton, InputAdornment, Select, FormControl, InputLabel,
  Alert, Stepper, Step, StepLabel, StepContent, Card, CardContent,
  Radio, RadioGroup, FormControlLabel, Avatar, LinearProgress,
  useMediaQuery, Grid, Fade, Zoom, AlertTitle
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  AttachMoney as MoneyIcon,
  Description as DescriptionIcon,
  Image as ImageIcon,
  Public as PublicIcon,
  Group as BubbleIcon,
  CheckCircle as CheckCircleIcon,
  Article as ArticleIcon,
  VideoLibrary as VideoIcon,
  DesignServices as DesignIcon,
  Code as CodeIcon,
  Campaign as CampaignIcon,
  Work as WorkIcon,
  PersonAdd as PersonAddIcon,
  People as PeopleIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  MusicNote as MusicNoteIcon,
  Palette as PaletteIcon,
  PhotoLibrary as PhotoLibraryIcon,
  ColorLens as ColorLensIcon,
  Add as AddIcon,
  Info as InfoIcon,
  AccountBalanceWallet as WalletIcon,
  Warning as WarningIcon,
  AddCircle as AddCircleIcon,
} from '@mui/icons-material';
import { useMutation, useQuery } from 'react-query';
import API from '../../../../API';
import useAuth from '../../../../Hooks/use-auth';
import MiniCreatorSearch from '../../../../Components/miniCreatorSearch';
import SelectAssetDialog from '../../../../Components/tools/selectfile';
import AddBalanceDialog from '../../../../Components/tools/addBalance';
import { getHeadersFromKeys, platforms, platPromoToHead, platPromoToKey, ToggleSet } from '../../../../Utils/constants';

const WORK_TYPES = [
  { value: 'video_editing', label: 'Video Editing', icon: <VideoIcon />, description: 'Edit and produce video content' },
  { value: 'graphic_design', label: 'Graphic Design', icon: <DesignIcon />, description: 'Create visual designs and graphics' },
  { value: 'social_media_management', label: 'Social Media Management', icon: <CampaignIcon />, description: 'Manage social accounts' },
  { value: 'copywriting', label: 'Copywriting', icon: <ArticleIcon />, description: 'Write engaging copy and content' },
  { value: 'photography', label: 'Photography', icon: <ImageIcon />, description: 'Professional photo services' },
  { value: 'web_development', label: 'Web Development', icon: <CodeIcon />, description: 'Build websites and web apps' },
  { value: 'motion_graphics', label: 'Motion Graphics', icon: <VideoIcon />, description: 'Create animated graphics' },
  { value: 'other', label: 'Other Services', icon: <WorkIcon />, description: 'Other professional services' },
];

const BRIEF_MIN_CHARS = 50;
const BRIEF_MAX_CHARS = 500;

const PRESET_THEMES = [
  { name: 'Ocean Blue', primary: '#0077be', secondary: '#00a8e8' },
  { name: 'Sunset Orange', primary: '#ff6b35', secondary: '#ff9f1c' },
  { name: 'Forest Green', primary: '#2d6a4f', secondary: '#52b788' },
  { name: 'Royal Purple', primary: '#5a189a', secondary: '#9d4edd' },
  { name: 'Rose Gold', primary: '#d4af37', secondary: '#f7cac9' },
  { name: 'Night Sky', primary: '#1a1a2e', secondary: '#16213e' },
  { name: 'Coral Reef', primary: '#ff6b6b', secondary: '#4ecdc4' },
  { name: 'Lavender Dreams', primary: '#a8dadc', secondary: '#e63946' },
];

const isSoundPromo = (promoKey) => {
  return promoKey && (
    promoKey.includes('sound') || 
    promoKey.includes('audio') ||
    promoKey === 'ig_reelsound' ||
    promoKey === 'tt_sound'
  );
};

const isBrandPromo = (promoKey) => {
  return promoKey && (
    promoKey.includes('brand') ||
    promoKey.includes('partnership') ||
    promoKey === 'ig_brandedcontent' ||
    promoKey === 'ig_paidpartnership'
  );
};

// ═══════════════════════════════════════════════════════════════════
// MEMOIZED PREVIEW CARD - OUTSIDE MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════
const PreviewCard = memo(({ 
  newCastingCall, 
  maxAffordableBudget, 
  isCreatorCall, 
  briefLength, 
  briefProgress, 
  briefValid, 
  briefTooLong, 
  selectedCreators, 
  bubbleName,
  activeStep,
  totalSteps,
  BRIEF_MAX_CHARS 
}) => {
  const theme = useTheme();
  
  return (
    <Card
      elevation={3}
      sx={{
        overflow: 'hidden',
        position: 'sticky',
        top: 16,
      }}
    >
      {/* Header Image Preview */}
      <Box
        sx={{
          height: 140,
          background: newCastingCall.headerImage
            ? `url(${newCastingCall.headerImage})`
            : `linear-gradient(135deg, ${newCastingCall.themeColors.primary} 0%, ${newCastingCall.themeColors.secondary} 100%)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {!newCastingCall.headerImage && (
          <Avatar
            sx={{
              width: 64,
              height: 64,
              bgcolor: 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)',
            }}
          >
            {isCreatorCall ? <CampaignIcon sx={{ fontSize: 32 }} /> : <WorkIcon sx={{ fontSize: 32 }} />}
          </Avatar>
        )}
      </Box>

      <CardContent sx={{ pb: 3 }}>
        <Stack spacing={2.5}>
          {/* Call Type */}
          <Box>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              {isCreatorCall ? 'Creator Casting Call' : 'General Work Call'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isCreatorCall ? 'For UGC content and influencer marketing' : 'For professional services and work'}
            </Typography>
          </Box>

          <Divider />

          {/* Budget */}
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              Maximum Budget
            </Typography>
            <Stack direction="row" alignItems="baseline" spacing={0.5}>
              <Typography variant="h4" fontWeight={800} color="success.main">
                ${newCastingCall.budget}
              </Typography>
              {maxAffordableBudget > 0 && (
                <Typography variant="caption" color="text.secondary">
                  / ${maxAffordableBudget}
                </Typography>
              )}
            </Stack>
            <LinearProgress
              variant="determinate"
              value={(newCastingCall.budget / Math.max(maxAffordableBudget, 1)) * 100}
              sx={{ mt: 1, height: 4, borderRadius: 2 }}
              color="success"
            />
          </Box>

          {/* Scope */}
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              Audience Scope
            </Typography>
            <Chip
              icon={newCastingCall.scopeType === 'global' ? <PublicIcon /> : <BubbleIcon />}
              label={newCastingCall.scopeType === 'global' ? 'Everyone' : (bubbleName || 'Bubble Members')}
              color="primary"
              size="small"
            />
          </Box>

          {/* Platforms/Work Types */}
          {isCreatorCall ? (
            <>
              {newCastingCall.platforms.length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    Platforms
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                    {newCastingCall.platforms.map((platform) => (
                      <Chip key={platform} label={platform} size="small" variant="outlined" color="primary" />
                    ))}
                  </Stack>
                </Box>
              )}
              {newCastingCall.promotionTypes.length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    Content Types
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                    {getHeadersFromKeys(newCastingCall.promotionTypes).slice(0, 3).map((type, idx) => (
                      <Chip key={idx} label={type} size="small" variant="outlined" color="secondary" />
                    ))}
                    {newCastingCall.promotionTypes.length > 3 && (
                      <Chip label={`+${newCastingCall.promotionTypes.length - 3}`} size="small" variant="outlined" />
                    )}
                  </Stack>
                </Box>
              )}
            </>
          ) : (
            newCastingCall.workTypes.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  Work Types
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                  {newCastingCall.workTypes.map((workType) => {
                    const type = WORK_TYPES.find(w => w.value === workType);
                    return (
                      <Chip key={workType} label={type?.label || workType} size="small" variant="outlined" color="primary" />
                    );
                  })}
                </Stack>
              </Box>
            )
          )}

          {/* Brief Preview */}
          {newCastingCall.brief && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                Brief
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {newCastingCall.brief}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={briefProgress}
                  color={briefTooLong ? "error" : briefValid ? "success" : "primary"}
                  sx={{ flex: 1, height: 4, borderRadius: 2 }}
                />
                <Typography variant="caption" color={briefTooLong ? "error" : briefValid ? "success.main" : "text.secondary"}>
                  {briefLength}/{BRIEF_MAX_CHARS}
                </Typography>
              </Stack>
            </Box>
          )}

          {/* Selected Creators */}
          {isCreatorCall && selectedCreators.size > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                Selected Creators
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                {[...selectedCreators].slice(0, 5).map((creator) => (
                  <Chip
                    key={creator}
                    avatar={<Avatar sx={{ bgcolor: 'secondary.main', width: 24, height: 24, fontSize: '0.75rem' }}>{creator[0]?.toUpperCase()}</Avatar>}
                    label={creator}
                    size="small"
                    variant="outlined"
                    color="secondary"
                  />
                ))}
                {selectedCreators.size > 5 && (
                  <Chip label={`+${selectedCreators.size - 5}`} size="small" variant="outlined" />
                )}
              </Stack>
            </Box>
          )}

          {/* Theme Colors */}
          {(newCastingCall.themeColors.primary || newCastingCall.themeColors.secondary) && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Theme Colors
              </Typography>
              <Stack direction="row" spacing={1}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    bgcolor: newCastingCall.themeColors.primary,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                />
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    bgcolor: newCastingCall.themeColors.secondary,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                />
              </Stack>
            </Box>
          )}

          {/* Progress */}
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Setup Progress
              </Typography>
              <Typography variant="caption" fontWeight={600} color="primary">
                {Math.round(((activeStep + 1) / totalSteps) * 100)}%
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={((activeStep + 1) / totalSteps) * 100}
              sx={{
                height: 6,
                borderRadius: 3,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
              }}
            />
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}, (prevProps, nextProps) => {
  // Custom comparison - only re-render if these specific props changed
  return (
    JSON.stringify(prevProps.newCastingCall) === JSON.stringify(nextProps.newCastingCall) &&
    prevProps.maxAffordableBudget === nextProps.maxAffordableBudget &&
    prevProps.isCreatorCall === nextProps.isCreatorCall &&
    prevProps.briefLength === nextProps.briefLength &&
    prevProps.briefProgress === nextProps.briefProgress &&
    prevProps.briefValid === nextProps.briefValid &&
    prevProps.briefTooLong === nextProps.briefTooLong &&
    JSON.stringify([...prevProps.selectedCreators]) === JSON.stringify([...nextProps.selectedCreators]) &&
    prevProps.bubbleName === nextProps.bubbleName &&
    prevProps.activeStep === nextProps.activeStep &&
    prevProps.totalSteps === nextProps.totalSteps
  );
});

// ═══════════════════════════════════════════════════════════════════
// MEMOIZED FORM CONTENT - OUTSIDE MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════
const CallFormContent = memo(({
  renderContextIndicator,
  activeStep,
  newCastingCall,
  handleCallTypeChange,
  isMobile,
  theme,
  mode,
  handleScopeChange,
  isGeneralCall,
  bubbleThemeColors,
  bubbleName,
  bubbles,
  bubblesLoading,
  isCreatorCall,
  handlePlatformChange,
  platforms,
  handlePromotionTypeChange,
  availablePromotionTypes,
  getPromoDisabledState,
  handleWorkTypeChange,
  renderBudgetAvailabilityAlert,
  handleInputChange,
  hasAnyFunds,
  briefLength,
  briefValid,
  briefTooLong,
  briefProgress,
  BRIEF_MAX_CHARS,
  BRIEF_MIN_CHARS,
  handleBudgetChange,
  maxAffordableBudget,
  isLoadingFunds,
  handleThemeSelect,
  handleThemeColorChange,
  handleOpenAssetDialog,
  handleRemoveSupportingImage,
  deliverableInput,
  setDeliverableInput,
  handleAddDeliverable,
  handleRemoveDeliverable,
  requirementInput,
  setRequirementInput,
  handleAddRequirement,
  handleRemoveRequirement,
  handlePickCreator,
  selectedCreators,
  setNewCastingCall,
  steps
}) => {
  return (
    <>
      {renderContextIndicator()}

      <Stepper activeStep={activeStep} orientation="vertical">
        {/* Step 0: Choose Type */}
        <Step>
          <StepLabel>Choose Casting Call Type</StepLabel>
          <StepContent TransitionProps={{ unmountOnExit: false }}>
            <RadioGroup
              value={newCastingCall.callType}
              onChange={handleCallTypeChange}
            >
              <Stack spacing={2}>
                <Fade in timeout={300}>
                  <Paper 
                    elevation={newCastingCall.callType === 'creator' ? 4 : 0}
                    sx={{ 
                      p: isMobile ? 2 : 2.5, 
                      border: '2px solid',
                      borderColor: newCastingCall.callType === 'creator' ? 'primary.main' : 'divider',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': { 
                        borderColor: 'primary.main',
                        transform: 'translateY(-2px)',
                        boxShadow: theme.shadows[4]
                      }
                    }}
                    onClick={() => handleCallTypeChange({ target: { value: 'creator' }})}
                  >
                    <FormControlLabel
                      value="creator"
                      control={<Radio />}
                      label={
                        <Box sx={{ ml: 1 }}>
                          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                            <CampaignIcon color="primary" />
                            <Typography 
                              variant={isMobile ? "subtitle1" : "h6"} 
                              fontWeight={700}
                            >
                              Creator Casting Call
                            </Typography>
                          </Stack>
                          <Typography 
                            variant={isMobile ? "caption" : "body2"} 
                            color="text.secondary" 
                            sx={{ mb: 1.5 }}
                          >
                            For UGC content and influencer marketing on social platforms
                          </Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                            <Chip size="small" label="Instagram" color="primary" variant="outlined" />
                            <Chip size="small" label="TikTok" color="primary" variant="outlined" />
                            <Chip size="small" label="YouTube" color="primary" variant="outlined" />
                          </Stack>
                        </Box>
                      }
                    />
                  </Paper>
                </Fade>

                <Fade in timeout={400}>
                  <Paper 
                    elevation={newCastingCall.callType === 'general' ? 4 : 0}
                    sx={{ 
                      p: isMobile ? 2 : 2.5,
                      border: '2px solid',
                      borderColor: newCastingCall.callType === 'general' ? 'primary.main' : 'divider',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': { 
                        borderColor: 'primary.main',
                        transform: 'translateY(-2px)',
                        boxShadow: theme.shadows[4]
                      }
                    }}
                    onClick={() => handleCallTypeChange({ target: { value: 'general' }})}
                  >
                    <FormControlLabel
                      value="general"
                      control={<Radio />}
                      label={
                        <Box sx={{ ml: 1 }}>
                          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                            <WorkIcon color="primary" />
                            <Typography 
                              variant={isMobile ? "subtitle1" : "h6"} 
                              fontWeight={700}
                            >
                              General Work Casting Call
                            </Typography>
                          </Stack>
                          <Typography 
                            variant={isMobile ? "caption" : "body2"} 
                            color="text.secondary" 
                            sx={{ mb: 1.5 }}
                          >
                            For design, editing, development, and professional services
                          </Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                            <Chip size="small" label="Design" color="primary" variant="outlined" />
                            <Chip size="small" label="Video Editing" color="primary" variant="outlined" />
                            <Chip size="small" label="Development" color="primary" variant="outlined" />
                          </Stack>
                        </Box>
                      }
                    />
                  </Paper>
                </Fade>
              </Stack>
            </RadioGroup>
          </StepContent>
        </Step>

        {/* Step 1: Scope */}
        <Step>
          <StepLabel>
            <Stack direction="row" alignItems="center" spacing={1}>
              <span>Audience Scope</span>
              {(mode === 'bubble' || mode === 'chat') && (
                <Chip 
                  label="Pre-selected" 
                  size="small" 
                  color="success" 
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              )}
            </Stack>
          </StepLabel>
          <StepContent TransitionProps={{ unmountOnExit: false }}>
            <Stack spacing={2.5}>
              {(mode === 'bubble' || mode === 'chat') ? (
                <Paper
                  elevation={0}
                  sx={{
                    p: isMobile ? 2 : 2.5,
                    border: '2px solid',
                    borderColor: 'primary.main',
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.05)
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar
                      sx={{
                        width: isMobile ? 40 : 48,
                        height: isMobile ? 40 : 48,
                        bgcolor: bubbleThemeColors?.primary || theme.palette.primary.main
                      }}
                    >
                      <BubbleIcon />
                    </Avatar>
                    <Box flex={1}>
                      <Typography 
                        variant={isMobile ? "body1" : "subtitle1"} 
                        fontWeight={700}
                      >
                        {bubbleName || 'Current Bubble'}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}
                      >
                        Casting call will be scoped to this bubble's members
                      </Typography>
                    </Box>
                    <Chip 
                      label="Active"
                      size="small"
                      color="primary"
                      sx={{ fontWeight: 600 }}
                    />
                  </Stack>
                </Paper>
              ) : (
                <>
                  {isGeneralCall && (
                    <Fade in>
                      <Alert severity="info" icon={<PublicIcon />}>
                        General casting calls require scope selection for proper targeting
                      </Alert>
                    </Fade>
                  )}
                  
                  <FormControl fullWidth>
                    <Select
                      value={newCastingCall.scopeType}
                      onChange={handleScopeChange}
                      sx={{ borderRadius: 2 }}
                    >
                      <MenuItem value="global">
                        <Stack direction="row" alignItems="center" spacing={2} sx={{ py: 1 }}>
                          <PublicIcon color="primary" />
                          <Box>
                            <Typography 
                              variant={isMobile ? "body2" : "body1"} 
                              fontWeight={600}
                            >
                              Everyone
                            </Typography>
                            <Typography 
                              variant="caption" 
                              color="text.secondary"
                              sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}
                            >
                              All {isCreatorCall ? 'creators' : 'users'} on the platform
                            </Typography>
                          </Box>
                        </Stack>
                      </MenuItem>
                      <MenuItem value="bubble">
                        <Stack direction="row" alignItems="center" spacing={2} sx={{ py: 1 }}>
                          <BubbleIcon color="primary" />
                          <Box>
                            <Typography 
                              variant={isMobile ? "body2" : "body1"} 
                              fontWeight={600}
                            >
                              My Community
                            </Typography>
                            <Typography 
                              variant="caption" 
                              color="text.secondary"
                              sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}
                            >
                              Only members of a specific community
                            </Typography>
                          </Box>
                        </Stack>
                      </MenuItem>
                    </Select>
                  </FormControl>

                  {newCastingCall.scopeType === 'bubble' && (
                    <Zoom in>
                      <FormControl fullWidth required>
                        <InputLabel>Select Community *</InputLabel>
                        <Select
                          value={newCastingCall.bubbleId || ""}
                          onChange={(e) => {
                            const selectedBubbleId = e.target.value;
                            const selectedBubbleData = bubbles.find(b => b.id === selectedBubbleId);
                            
                            setNewCastingCall(prev => ({ 
                              ...prev, 
                              bubbleId: selectedBubbleId,
                              conversationId: selectedBubbleData?.conversation_id || null
                            }));
                          }}
                          label="Select Community *"
                          disabled={bubblesLoading}
                          sx={{ borderRadius: 2 }}
                        >
                          {bubbles.map((bubble) => (
                            <MenuItem key={bubble.id} value={bubble.id}>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <BubbleIcon fontSize="small" color="action" />
                                <Typography>{bubble.name}</Typography>
                                {bubble.has_chat && bubble.conversation_id && (
                                  <Chip 
                                    label="Chat" 
                                    size="small" 
                                    sx={{ ml: 'auto', height: 20, fontSize: '0.65rem' }}
                                  />
                                )}
                              </Stack>
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>
                          Choose which community can see and apply
                        </FormHelperText>
                      </FormControl>
                    </Zoom>
                  )}
                </>
              )}
            </Stack>
          </StepContent>
        </Step>

        {/* Step 2: Type-Specific Fields */}
        <Step>
          <StepLabel>
            {isCreatorCall ? 'Platform & Content Type' : 'Work Type Selection'}
          </StepLabel>
          <StepContent TransitionProps={{ unmountOnExit: false }}>
            {isCreatorCall ? (
              <Stack spacing={3}>
                <TextField
                  select
                  label='Select Platforms *'
                  fullWidth
                  value={newCastingCall.platforms}
                  onChange={handlePlatformChange}
                  required
                  SelectProps={{
                    multiple: true,
                    MenuProps: {
                      disablePortal: true,
                      keepMounted: false,
                    },
                    renderValue: (selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip 
                            key={value} 
                            label={value} 
                            size="small"
                            color="primary"
                          />
                        ))}
                      </Box>
                    )
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                >
                  {platforms.map((platform) => (
                    <MenuItem key={platform} value={platform}>
                      <Checkbox checked={newCastingCall.platforms.includes(platform)} />
                      <ListItemText primary={platform} />
                    </MenuItem>
                  ))}
                </TextField>

                <Box>
                  <TextField
                    select
                    label='Select Content Types *'
                    fullWidth
                    value={newCastingCall.promotionTypes}
                    onChange={handlePromotionTypeChange}
                    required
                    disabled={newCastingCall.platforms.length === 0}
                    SelectProps={{
                      multiple: true,
                      MenuProps: {
                        disablePortal: true,
                        keepMounted: false,
                      },
                      renderValue: (selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {getHeadersFromKeys(selected).map((value, index) => (
                            <Chip 
                              key={index} 
                              label={value} 
                              size="small"
                              color="secondary"
                            />
                          ))}
                        </Box>
                      )
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  >
                    {availablePromotionTypes.map((type) => {
                      const isDisabled = getPromoDisabledState(type.key);
                      const isSound = isSoundPromo(type.key);
                      const isBrand = isBrandPromo(type.key);
                      
                      return (
                        <MenuItem 
                          key={type.key} 
                          value={type.key}
                          disabled={isDisabled}
                        >
                          <Checkbox 
                            checked={newCastingCall.promotionTypes.includes(type.key)} 
                            disabled={isDisabled}
                          />
                          {isSound && <MusicNoteIcon fontSize="small" sx={{ mr: 1, color: 'secondary.main' }} />}
                          {isBrand && <PaletteIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />}
                          <ListItemText 
                            primary={type.header}
                            secondary={isDisabled ? 
                              (isBrand ? "Disabled: Sound promo selected" : "Disabled: Brand promo selected") 
                              : undefined
                            }
                          />
                        </MenuItem>
                      );
                    })}
                  </TextField>
                  
                  {newCastingCall.promotionTypes.some(isSoundPromo) && (
                    <Alert severity="info" sx={{ mt: 1.5 }} icon={<MusicNoteIcon />}>
                      Sound promotions selected. Brand promotions are disabled.
                    </Alert>
                  )}
                  {newCastingCall.promotionTypes.some(isBrandPromo) && (
                    <Alert severity="info" sx={{ mt: 1.5 }} icon={<PaletteIcon />}>
                      Brand promotions selected. Sound promotions are disabled.
                    </Alert>
                  )}
                </Box>
              </Stack>
            ) : (
              <Box>
                <TextField
                  select
                  label='Select Work Types *'
                  fullWidth
                  value={newCastingCall.workTypes}
                  onChange={handleWorkTypeChange}
                  required
                  SelectProps={{
                    multiple: true,
                    MenuProps: {
                      disablePortal: true,
                      keepMounted: false,
                    },
                    renderValue: (selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => {
                          const workType = WORK_TYPES.find(w => w.value === value);
                          return (
                            <Chip 
                              key={value} 
                              label={workType?.label || value} 
                              size="small"
                              color="primary"
                            />
                          );
                        })}
                      </Box>
                    )
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                >
                  {WORK_TYPES.map((workType) => (
                    <MenuItem key={workType.value} value={workType.value}>
                      <Checkbox checked={newCastingCall.workTypes.includes(workType.value)} />
                      <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                        {workType.icon}
                      </Box>
                      <ListItemText 
                        primary={workType.label}
                        secondary={workType.description}
                      />
                    </MenuItem>
                  ))}
                </TextField>
                <FormHelperText sx={{ mt: 1, ml: 2 }}>
                  Select one or more types of work needed
                </FormHelperText>
              </Box>
            )}
          </StepContent>
        </Step>

        {/* Step 3: Project Details */}
        <Step>
          <StepLabel>Project Details</StepLabel>
          <StepContent TransitionProps={{ unmountOnExit: false }}>
            <Stack spacing={3}>
              {renderBudgetAvailabilityAlert()}

              <Box>
                <TextField
                  label="Project Brief"
                  name="brief"
                  fullWidth
                  multiline
                  rows={4}
                  value={newCastingCall.brief}
                  onChange={handleInputChange}
                  required
                  disabled={!hasAnyFunds}
                  error={!briefValid && briefLength > 0}
                  placeholder="Describe what you're looking for in detail..."
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography 
                          variant="caption" 
                          color={briefTooLong ? "error" : briefValid ? "success.main" : "text.secondary"}
                        >
                          {briefLength}/{BRIEF_MAX_CHARS}
                        </Typography>
                      </InputAdornment>
                    )
                  }}
                />
                
                <Box sx={{ mt: 1 }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={briefProgress}
                    color={briefTooLong ? "error" : briefValid ? "success" : "primary"}
                    sx={{ 
                      height: 4, 
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.1)
                    }}
                  />
                  <Typography 
                    variant="caption" 
                    color={briefTooLong ? "error" : briefValid ? "success.main" : "text.secondary"}
                    sx={{ mt: 0.5, display: 'block' }}
                  >
                    {briefTooLong 
                      ? `Too long! Remove ${briefLength - BRIEF_MAX_CHARS} characters`
                      : briefValid 
                      ? "✓ Brief meets minimum length" 
                      : `${BRIEF_MIN_CHARS - briefLength} more characters needed`
                    }
                  </Typography>
                </Box>
              </Box>

              <TextField
                label="Additional Details (Optional)"
                name="description"
                fullWidth
                multiline
                rows={3}
                value={newCastingCall.description}
                onChange={handleInputChange}
                disabled={!hasAnyFunds}
                placeholder="Add more context, goals, or expectations..."
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />

              <Paper 
                elevation={0}
                sx={{ 
                  p: isMobile ? 2 : 2.5, 
                  border: '1px solid',
                  borderColor: hasAnyFunds ? 'divider' : 'error.main',
                  borderRadius: 2,
                  bgcolor: hasAnyFunds 
                    ? alpha(theme.palette.success.main, 0.05)
                    : alpha(theme.palette.error.main, 0.05)
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography 
                    variant={isMobile ? "body1" : "subtitle1"} 
                    fontWeight={600}
                  >
                    Maximum Budget
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip 
                      label={`$${newCastingCall.budget}`}
                      color={hasAnyFunds ? "success" : "default"}
                      sx={{ 
                        fontWeight: 700, 
                        fontSize: isMobile ? '0.9rem' : '1.1rem',
                        height: isMobile ? 28 : 32
                      }}
                    />
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}
                    >
                      / ${maxAffordableBudget}
                    </Typography>
                  </Stack>
                </Stack>
                
                {!hasAnyFunds && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    You must add balance to set a budget
                  </Alert>
                )}

                <Slider
                  value={newCastingCall.budget}
                  onChange={handleBudgetChange}
                  min={0}
                  max={Math.max(maxAffordableBudget, 100)}
                  step={50}
                  disabled={!hasAnyFunds || isLoadingFunds}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => `$${value}`}
                  marks={
                    hasAnyFunds ? [
                      { value: 0, label: '$0' },
                      { value: Math.floor(maxAffordableBudget / 2), label: `$${Math.floor(maxAffordableBudget / 2)}` },
                      { value: maxAffordableBudget, label: `$${maxAffordableBudget}` }
                    ] : [
                      { value: 0, label: '$0' }
                    ]
                  }
                  sx={{
                    '& .MuiSlider-thumb': { 
                      width: isMobile ? 18 : 20, 
                      height: isMobile ? 18 : 20,
                      transition: 'all 0.2s'
                    },
                    '& .MuiSlider-track': { height: isMobile ? 4 : 6 },
                    '& .MuiSlider-rail': { height: isMobile ? 4 : 6 }
                  }}
                />
                <FormHelperText sx={{ mt: 1 }}>
                  {hasAnyFunds 
                    ? `Set your maximum budget up to $${maxAffordableBudget}. ${isCreatorCall ? 'Creators' : 'Applicants'} can propose amounts below this.`
                    : 'Add balance to set a budget for this casting call.'
                  }
                </FormHelperText>
              </Paper>
            </Stack>
          </StepContent>
        </Step>

        {/* Step 4: Branding & Extras */}
        <Step>
          <StepLabel optional={<Typography variant="caption">Optional</Typography>}>
            Branding & Additional Information
          </StepLabel>
          <StepContent TransitionProps={{ unmountOnExit: false }}>
            <Stack spacing={3}>
              <Box>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                  <ColorLensIcon color="primary" />
                  <Typography 
                    variant={isMobile ? "body1" : "subtitle1"} 
                    fontWeight={700}
                  >
                    Theme Colors
                  </Typography>
                </Stack>
                
                <Typography 
                  variant={isMobile ? "caption" : "body2"} 
                  color="text.secondary" 
                  sx={{ mb: 2 }}
                >
                  Choose colors for your casting call page
                </Typography>

                <Grid container spacing={1.5} sx={{ mb: 2 }}>
                  {PRESET_THEMES.map((presetTheme) => (
                    <Grid item xs={6} sm={3} key={presetTheme.name}>
                      <Paper
                        onClick={() => handleThemeSelect(presetTheme)}
                        sx={{
                          p: isMobile ? 1 : 1.5,
                          cursor: 'pointer',
                          border: '2px solid',
                          borderColor: 
                            newCastingCall.themeColors.primary === presetTheme.primary && 
                            newCastingCall.themeColors.secondary === presetTheme.secondary
                            ? 'primary.main' 
                            : 'divider',
                          borderRadius: 2,
                          transition: 'all 0.2s',
                          '&:hover': {
                            transform: 'scale(1.05)',
                            boxShadow: theme.shadows[4]
                          }
                        }}
                      >
                        <Stack direction="row" spacing={0.5} sx={{ mb: 1 }}>
                          <Box 
                            sx={{ 
                              width: '50%', 
                              height: isMobile ? 32 : 40, 
                              bgcolor: presetTheme.primary,
                              borderRadius: 1
                            }} 
                          />
                          <Box 
                            sx={{ 
                              width: '50%', 
                              height: isMobile ? 32 : 40, 
                              bgcolor: presetTheme.secondary,
                              borderRadius: 1
                            }} 
                          />
                        </Stack>
                        <Typography 
                          variant="caption" 
                          fontWeight={600} 
                          sx={{ 
                            display: 'block', 
                            textAlign: 'center',
                            fontSize: isMobile ? '0.65rem' : '0.75rem'
                          }}
                        >
                          {presetTheme.name}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Primary Color"
                      type="color"
                      fullWidth
                      value={newCastingCall.themeColors.primary}
                      onChange={(e) => handleThemeColorChange('primary', e.target.value)}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Secondary Color"
                      type="color"
                      fullWidth
                      value={newCastingCall.themeColors.secondary}
                      onChange={(e) => handleThemeColorChange('secondary', e.target.value)}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              <Box>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                  <PhotoLibraryIcon color="primary" />
                  <Typography 
                    variant={isMobile ? "body1" : "subtitle1"} 
                    fontWeight={700}
                  >
                    Images
                  </Typography>
                </Stack>
                
                <Box sx={{ mb: 3 }}>
                  <Typography 
                    variant="subtitle2" 
                    fontWeight={600} 
                    sx={{ mb: 1.5, fontSize: isMobile ? '0.875rem' : '1rem' }}
                  >
                    Header Image
                  </Typography>
                  
                  {newCastingCall.headerImage ? (
                    <Paper 
                      elevation={0}
                      sx={{ 
                        p: isMobile ? 1.5 : 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2
                      }}
                    >
                      <Avatar
                        variant="rounded"
                        src={newCastingCall.headerImage}
                        sx={{ 
                          width: isMobile ? 48 : 60, 
                          height: isMobile ? 48 : 60 
                        }}
                      >
                        <ImageIcon />
                      </Avatar>
                      <Typography 
                        variant={isMobile ? "caption" : "body2"} 
                        sx={{ 
                          flex: 1, 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis' 
                        }}
                      >
                        {newCastingCall.headerImage}
                      </Typography>
                      <IconButton 
                        size="small" 
                        onClick={() => setNewCastingCall(prev => ({ ...prev, headerImage: "" }))}
                        color="error"
                      >
                        <DeleteIcon fontSize={isMobile ? "small" : "medium"} />
                      </IconButton>
                    </Paper>
                  ) : (
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<AddIcon />}
                      onClick={() => handleOpenAssetDialog('header')}
                      sx={{ 
                        borderRadius: 2, 
                        py: isMobile ? 1.25 : 1.5,
                        fontSize: isMobile ? '0.875rem' : '1rem'
                      }}
                    >
                      Select Header Image
                    </Button>
                  )}
                </Box>

                <Box>
                  <Typography 
                    variant="subtitle2" 
                    fontWeight={600} 
                    sx={{ mb: 1.5, fontSize: isMobile ? '0.875rem' : '1rem' }}
                  >
                    Supporting Images
                  </Typography>
                  
                  {newCastingCall.supportingImages.length > 0 && (
                    <Stack spacing={1} sx={{ mb: 1.5 }}>
                      {newCastingCall.supportingImages.map((image, index) => (
                        <Paper 
                          key={index}
                          elevation={0}
                          sx={{ 
                            p: isMobile ? 1.25 : 1.5,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2
                          }}
                        >
                          <Avatar
                            variant="rounded"
                            src={image}
                            sx={{ 
                              width: isMobile ? 32 : 40, 
                              height: isMobile ? 32 : 40 
                            }}
                          >
                            <ImageIcon fontSize="small" />
                          </Avatar>
                          <Typography 
                            variant={isMobile ? "caption" : "body2"} 
                            sx={{ 
                              flex: 1, 
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis', 
                              fontSize: isMobile ? '0.75rem' : '0.875rem' 
                            }}
                          >
                            {image}
                          </Typography>
                          <IconButton 
                            size="small" 
                            onClick={() => handleRemoveSupportingImage(index)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Paper>
                      ))}
                    </Stack>
                  )}
                  
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenAssetDialog('supporting')}
                    sx={{ 
                      borderRadius: 2, 
                      py: isMobile ? 1.25 : 1.5,
                      fontSize: isMobile ? '0.875rem' : '1rem'
                    }}
                  >
                    Add Supporting Image
                  </Button>
                </Box>
              </Box>

              <Divider />

              <TextField
                label="Timeline"
                fullWidth
                value={newCastingCall.deliverablesScope.timeline}
                onChange={(e) => setNewCastingCall(prev => ({
                  ...prev,
                  deliverablesScope: {
                    ...prev.deliverablesScope,
                    timeline: e.target.value
                  }
                }))}
                placeholder="e.g., 2 weeks, 1 month"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CheckCircleIcon color="action" />
                    </InputAdornment>
                  )
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />

              <Box>
                <Typography 
                  variant="subtitle2" 
                  fontWeight={600} 
                  sx={{ mb: 1.5, fontSize: isMobile ? '0.875rem' : '1rem' }}
                >
                  Deliverables
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                  <TextField
                    fullWidth
                    size="small"
                    value={deliverableInput}
                    onChange={(e) => setDeliverableInput(e.target.value)}
                    placeholder={isCreatorCall ? "3 Instagram posts" : "5 edited videos"}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddDeliverable())}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                  <Button 
                    variant="contained" 
                    onClick={handleAddDeliverable}
                    disabled={!deliverableInput.trim()}
                    sx={{ 
                      borderRadius: 2, 
                      minWidth: isMobile ? 70 : 80,
                      fontSize: isMobile ? '0.875rem' : '1rem'
                    }}
                  >
                    Add
                  </Button>
                </Stack>
                <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                  {newCastingCall.deliverablesScope.deliverables.map((deliverable, index) => (
                    <Chip
                      key={index}
                      label={deliverable}
                      onDelete={() => handleRemoveDeliverable(index)}
                      variant="outlined"
                      color="primary"
                      size={isMobile ? "small" : "medium"}
                    />
                  ))}
                </Stack>
              </Box>

              <Box>
                <Typography 
                  variant="subtitle2" 
                  fontWeight={600} 
                  sx={{ mb: 1.5, fontSize: isMobile ? '0.875rem' : '1rem' }}
                >
                  Requirements
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                  <TextField
                    fullWidth
                    size="small"
                    value={requirementInput}
                    onChange={(e) => setRequirementInput(e.target.value)}
                    placeholder={isCreatorCall ? "10k+ followers" : "3+ years experience"}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddRequirement())}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                  <Button 
                    variant="contained" 
                    onClick={handleAddRequirement}
                    disabled={!requirementInput.trim()}
                    sx={{ 
                      borderRadius: 2, 
                      minWidth: isMobile ? 70 : 80,
                      fontSize: isMobile ? '0.875rem' : '1rem'
                    }}
                  >
                    Add
                  </Button>
                </Stack>
                <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                  {newCastingCall.deliverablesScope.requirements.map((requirement, index) => (
                    <Chip
                      key={index}
                      label={requirement}
                      onDelete={() => handleRemoveRequirement(index)}
                      variant="outlined"
                      color="secondary"
                      size={isMobile ? "small" : "medium"}
                    />
                  ))}
                </Stack>
              </Box>
            </Stack>
          </StepContent>
        </Step>

        {/* Step 5: Creator Invitations */}
        {isCreatorCall && (
          <Step>
            <StepLabel optional={<Typography variant="caption">Optional</Typography>}>
              Invite Specific Creators
            </StepLabel>
            <StepContent TransitionProps={{ unmountOnExit: false }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Invite specific creators now, or add them later.
              </Alert>

              <Box sx={{ height: isMobile ? '40vh' : '60vh', minHeight: 300 }}>
                <Stack spacing={2} sx={{ height: '100%' }}>
                  <Paper 
                    sx={{ 
                      flex: 1,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                    elevation={0}
                  >
                    <Box 
                      sx={{ 
                        p: isMobile ? 1.25 : 1.5,
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                        borderBottom: '1px solid',
                        borderColor: 'divider'
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <SearchIcon color="primary" fontSize="small" />
                        <Typography 
                          variant="subtitle2" 
                          fontWeight={600}
                          sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}
                        >
                          Search Creators
                        </Typography>
                      </Stack>
                    </Box>

                    <Box sx={{ flex: 1, overflow: 'hidden' }}>
                      <MiniCreatorSearch
                        searchSettings={{ promotion_types: newCastingCall.promotionTypes }}
                        selectedCreators={selectedCreators}
                        disabledCreators={new Set()}
                        onCreatorPick={handlePickCreator}
                      />
                    </Box>
                  </Paper>

                  {selectedCreators.size > 0 && (
                    <Paper 
                      sx={{ 
                        p: isMobile ? 1.25 : 1.5,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        maxHeight: 200,
                        overflow: 'auto'
                      }}
                      elevation={0}
                    >
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                        <PeopleIcon color="secondary" fontSize="small" />
                        <Typography 
                          variant="subtitle2" 
                          fontWeight={600}
                          sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}
                        >
                          Selected ({selectedCreators.size})
                        </Typography>
                      </Stack>

                      <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                        {[...selectedCreators].map((creator) => (
                          <Chip
                            key={creator}
                            avatar={
                              <Avatar 
                                sx={{ 
                                  bgcolor: 'secondary.main', 
                                  width: isMobile ? 20 : 24, 
                                  height: isMobile ? 20 : 24, 
                                  fontSize: isMobile ? '0.65rem' : '0.75rem' 
                                }}
                              >
                                {creator[0]?.toUpperCase()}
                              </Avatar>
                            }
                            label={creator}
                            onDelete={() => handlePickCreator({ creator })}
                            size="small"
                            color="secondary"
                            variant="outlined"
                          />
                        ))}
                      </Stack>
                    </Paper>
                  )}
                </Stack>
              </Box>
            </StepContent>
          </Step>
        )}
      </Stepper>
    </>
  );
});

export default function CreateCallDialog({ 
  open, 
  handleClose, 
  onSuccess,
  mode = 'standalone',
  bubbleId = null,
  bubbleName = null,
  bubbleThemeColors = null,
  conversationId = null,
  defaultScopeType = 'global',
  autoSelectBubble = false
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const { getCurrrentUser } = useAuth();
  const currentUser = getCurrrentUser();
  
  const [activeStep, setActiveStep] = useState(0);
  const [showAddBalanceDialog, setShowAddBalanceDialog] = useState(false);
  const [deliverableInput, setDeliverableInput] = useState("");
  const [requirementInput, setRequirementInput] = useState("");
  const [assetDialogOpen, setAssetDialogOpen] = useState(false);
  const [assetSelectionMode, setAssetSelectionMode] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState(null);

  // ═══════════════════════════════════════════════════════════════════
  // FETCH BALANCE AND CREDIT INFO
  // ═══════════════════════════════════════════════════════════════════
  
  const { 
    data: balanceData, 
    isLoading: balanceLoading,
    refetch: refetchBalance
  } = useQuery(
    ["myBalance"],
    () => API.balanceLedger.fetchMyBalance(),
    {
      enabled: open && !!currentUser?.id,
      staleTime: 30000,
      refetchOnWindowFocus: false,
    }
  );

  const { 
    data: companyInfo,
    isLoading: companyLoading
  } = useQuery(
    ["company", currentUser?.company_id],
    () => API.companies.listFetch({ id: currentUser?.company_id }),
    { 
      enabled: open && !!currentUser?.company_id,
      staleTime: 60000,
      refetchOnWindowFocus: false,
    }
  );

  const hasBlitzPayAccount = companyInfo?.blitzpay_account === true;
  
  const { 
    data: spendingInfo,
    isLoading: spendingLoading
  } = useQuery(
    ["mySpendingInfo"],
    () => API.teams.getMySpendingInfo(),
    {
      enabled: open && hasBlitzPayAccount,
      staleTime: 60000,
      refetchOnWindowFocus: false,
    }
  );

  // ═══════════════════════════════════════════════════════════════════
  // CALCULATE MAXIMUM AFFORDABLE BUDGET
  // ═══════════════════════════════════════════════════════════════════
  
  const maxAffordableBudget = useMemo(() => {
    const currentBalance = parseFloat(balanceData?.balance || 0);
    
    if (hasBlitzPayAccount && spendingInfo) {
      const availableSpending = parseFloat(spendingInfo.total_available_funds || 0);
      return Math.floor(currentBalance + availableSpending);
    }

    return Math.floor(currentBalance);
  }, [balanceData, spendingInfo, hasBlitzPayAccount]);

  const hasAnyFunds = maxAffordableBudget > 0;
  const isLoadingFunds = balanceLoading || companyLoading || (hasBlitzPayAccount && spendingLoading);

  // ═══════════════════════════════════════════════════════════════════
  // STATE INITIALIZATION
  // ═══════════════════════════════════════════════════════════════════
  
  const getInitialState = useCallback(() => {
    const initialBudget = hasAnyFunds 
      ? Math.min(500, maxAffordableBudget) 
      : 0;

    const baseState = {
      callType: 'creator',
      brief: "",
      budget: initialBudget,
      description: "",
      scopeType: defaultScopeType,
      bubbleId: autoSelectBubble ? bubbleId : null,
      conversationId: null,
      platforms: [],
      promotionTypes: [],
      workTypes: [],
      headerImage: "",
      supportingImages: [],
      themeColors: {
        primary: bubbleThemeColors?.primary || theme.palette.primary.main,
        secondary: bubbleThemeColors?.secondary || theme.palette.secondary.main
      },
      deliverablesScope: {
        deliverables: [],
        timeline: "",
        requirements: []
      }
    };

    if ((mode === 'bubble' || mode === 'chat') && bubbleId) {
      baseState.scopeType = 'bubble';
      baseState.bubbleId = bubbleId;
      baseState.conversationId = conversationId;
    }

    return baseState;
  }, [hasAnyFunds, maxAffordableBudget, defaultScopeType, autoSelectBubble, bubbleId, mode, conversationId, bubbleThemeColors, theme.palette.primary.main, theme.palette.secondary.main]);

  const [newCastingCall, setNewCastingCall] = useState(getInitialState);
  const [selectedCreators, setSelectedCreators] = useState(new Set());

  useEffect(() => {
    if (hasAnyFunds && newCastingCall.budget === 0) {
      setNewCastingCall(prev => ({
        ...prev,
        budget: Math.min(500, maxAffordableBudget)
      }));
    } else if (newCastingCall.budget > maxAffordableBudget) {
      setNewCastingCall(prev => ({
        ...prev,
        budget: maxAffordableBudget
      }));
    }
  }, [maxAffordableBudget, hasAnyFunds, newCastingCall.budget]);

  const { data: bubblesData, isLoading: bubblesLoading } = useQuery(
    ["bubbles-list"],
    () => API.bubbles.list(),
    {
      enabled: open && newCastingCall.scopeType === 'bubble' && mode === 'standalone',
      staleTime: 60000,
      refetchOnWindowFocus: false,
    }
  );

  const bubbles = bubblesData?.bubbles || [];

  const { mutate: postNewCastingCall, isLoading: isPosting } = useMutation(
    API.castingCalls.create,
    {
      onSuccess: (newCall) => {
        setNewCastingCall(getInitialState());
        setActiveStep(0);
        setSelectedCreators(new Set());
        
        if (onSuccess) {
          onSuccess(newCall);
        }
        
        handleClose();
      },
      onError: (error) => {
        console.error("Error creating casting call:", error);
        alert(`An error occurred: ${error?.response?.data?.error || error.message}`);
      },
    }
  );

  // Memoized event handlers - prevent recreation on every render
  const handleInputChange = useCallback((event) => {
    const { name, value } = event.target;
    setNewCastingCall((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleCallTypeChange = useCallback((event) => {
    const callType = event.target.value;
    setNewCastingCall((prev) => ({
      ...prev,
      callType,
      platforms: callType === 'creator' ? prev.platforms : [],
      promotionTypes: callType === 'creator' ? prev.promotionTypes : [],
      workTypes: callType === 'general' ? prev.workTypes : []
    }));
  }, []);

  const handlePlatformChange = useCallback((event) => {
    const { value } = event.target;
    setNewCastingCall((prev) => ({
      ...prev,
      platforms: value,
      promotionTypes: [],
    }));
  }, []);

  const handlePromotionTypeChange = useCallback((event) => {
    const { value } = event.target;
    const newValues = typeof value === "string" ? value.split(",") : value;
    
    const hasSoundPromo = newValues.some(v => isSoundPromo(v));
    const hasBrandPromo = newValues.some(v => isBrandPromo(v));
    
    let filteredValues = newValues;
    if (hasSoundPromo) {
      filteredValues = newValues.filter(v => !isBrandPromo(v));
    }
    if (hasBrandPromo) {
      filteredValues = newValues.filter(v => !isSoundPromo(v));
    }
    
    setNewCastingCall((prev) => ({
      ...prev,
      promotionTypes: filteredValues,
    }));
  }, []);

  const handleWorkTypeChange = useCallback((event) => {
    const { value } = event.target;
    setNewCastingCall((prev) => ({
      ...prev,
      workTypes: typeof value === "string" ? value.split(",") : value,
    }));
  }, []);

  const handleBudgetChange = useCallback((event, value) => {
    const cappedValue = Math.min(value, maxAffordableBudget);
    setNewCastingCall((prev) => ({ ...prev, budget: cappedValue }));
  }, [maxAffordableBudget]);

  const handleScopeChange = useCallback((event) => {
    const scopeType = event.target.value;
    setNewCastingCall((prev) => ({
      ...prev,
      scopeType,
      bubbleId: scopeType === 'bubble' ? prev.bubbleId : null
    }));
  }, []);

  const handleThemeSelect = useCallback((presetTheme) => {
    setNewCastingCall((prev) => ({
      ...prev,
      themeColors: {
        primary: presetTheme.primary,
        secondary: presetTheme.secondary
      }
    }));
  }, []);

  const handleThemeColorChange = useCallback((colorType, value) => {
    setNewCastingCall((prev) => ({
      ...prev,
      themeColors: {
        ...prev.themeColors,
        [colorType]: value
      }
    }));
  }, []);

  const handleOpenAssetDialog = useCallback((mode) => {
    setAssetSelectionMode(mode);
    setAssetDialogOpen(true);
  }, []);

  const handleAssetClick = useCallback((asset) => {
    setSelectedAsset(asset);
  }, []);

  const handleAssetDialogClose = useCallback(() => {
    if (selectedAsset) {
      if (assetSelectionMode === 'header') {
        setNewCastingCall((prev) => ({
          ...prev,
          headerImage: selectedAsset.file_url || selectedAsset.filename
        }));
      } else if (assetSelectionMode === 'supporting') {
        setNewCastingCall((prev) => ({
          ...prev,
          supportingImages: [...prev.supportingImages, selectedAsset.file_url || selectedAsset.filename]
        }));
      }
    }
    
    setAssetDialogOpen(false);
    setSelectedAsset(null);
    setAssetSelectionMode(null);
  }, [selectedAsset, assetSelectionMode]);

  const handleRemoveSupportingImage = useCallback((index) => {
    setNewCastingCall((prev) => ({
      ...prev,
      supportingImages: prev.supportingImages.filter((_, i) => i !== index)
    }));
  }, []);

  const handleAddDeliverable = useCallback(() => {
    if (deliverableInput.trim()) {
      setNewCastingCall((prev) => ({
        ...prev,
        deliverablesScope: {
          ...prev.deliverablesScope,
          deliverables: [...prev.deliverablesScope.deliverables, deliverableInput.trim()]
        }
      }));
      setDeliverableInput("");
    }
  }, [deliverableInput]);

  const handleRemoveDeliverable = useCallback((index) => {
    setNewCastingCall((prev) => ({
      ...prev,
      deliverablesScope: {
        ...prev.deliverablesScope,
        deliverables: prev.deliverablesScope.deliverables.filter((_, i) => i !== index)
      }
    }));
  }, []);

  const handleAddRequirement = useCallback(() => {
    if (requirementInput.trim()) {
      setNewCastingCall((prev) => ({
        ...prev,
        deliverablesScope: {
          ...prev.deliverablesScope,
          requirements: [...prev.deliverablesScope.requirements, requirementInput.trim()]
        }
      }));
      setRequirementInput("");
    }
  }, [requirementInput]);

  const handleRemoveRequirement = useCallback((index) => {
    setNewCastingCall((prev) => ({
      ...prev,
      deliverablesScope: {
        ...prev.deliverablesScope,
        requirements: prev.deliverablesScope.requirements.filter((_, i) => i !== index)
      }
    }));
  }, []);

  const handlePickCreator = useCallback((creator) => {
    ToggleSet(creator.creator, selectedCreators, setSelectedCreators);
  }, [selectedCreators]);

  const handleSubmit = useCallback(() => {
    const payload = {
      callType: newCastingCall.callType,
      brief: newCastingCall.brief,
      budget: newCastingCall.budget,
    };

    if (newCastingCall.description) {
      payload.description = newCastingCall.description;
    }

    if (mode === 'bubble' || mode === 'chat') {
      payload.bubbleId = bubbleId;
      payload.mode = mode;
      
      if (conversationId) {
        payload.conversationId = conversationId;
      }
    } else if (newCastingCall.scopeType === 'bubble' && newCastingCall.bubbleId) {
      payload.bubbleId = newCastingCall.bubbleId;
      
      if (newCastingCall.conversationId) {
        payload.conversationId = newCastingCall.conversationId;
      }
    }

    if (newCastingCall.callType === 'creator') {
      payload.platforms = newCastingCall.platforms;
      payload.promotionTypes = newCastingCall.promotionTypes;
      
      if (selectedCreators.size > 0) {
        payload.invitedCreators = [...selectedCreators];
      }
    } else {
      payload.workTypes = newCastingCall.workTypes;
    }

    if (newCastingCall.headerImage) {
      payload.headerImage = newCastingCall.headerImage;
    }
    if (newCastingCall.supportingImages.length > 0) {
      payload.supportingImages = newCastingCall.supportingImages;
    }

    if (newCastingCall.themeColors.primary || newCastingCall.themeColors.secondary) {
      payload.themeColors = newCastingCall.themeColors;
    }

    if (newCastingCall.deliverablesScope.deliverables.length > 0 || 
        newCastingCall.deliverablesScope.requirements.length > 0 ||
        newCastingCall.deliverablesScope.timeline) {
      payload.deliverablesScope = newCastingCall.deliverablesScope;
    }

    console.log('📤 Submitting casting call payload:', payload);
    postNewCastingCall(payload);
  }, [newCastingCall, mode, bubbleId, conversationId, selectedCreators, postNewCastingCall]);

  const handleAddBalanceSuccess = useCallback(() => {
    refetchBalance();
    setShowAddBalanceDialog(false);
  }, [refetchBalance]);

  const availablePromotionTypes = useMemo(() => {
    return newCastingCall.platforms.flatMap((platform) => {
      if (platPromoToKey[platform] && platPromoToHead[platform]) {
        return Object.keys(platPromoToKey[platform]).map((promo) => ({
          key: platPromoToKey[platform][promo],
          header: platPromoToHead[platform][promo],
        }));
      }
      return [];
    });
  }, [newCastingCall.platforms]);

  const getPromoDisabledState = useCallback((promoKey) => {
    const currentlyHasSoundPromo = newCastingCall.promotionTypes.some(v => isSoundPromo(v));
    const currentlyHasBrandPromo = newCastingCall.promotionTypes.some(v => isBrandPromo(v));
    
    if (currentlyHasSoundPromo && isBrandPromo(promoKey)) return true;
    if (currentlyHasBrandPromo && isSoundPromo(promoKey)) return true;
    
    return false;
  }, [newCastingCall.promotionTypes]);

  const isCreatorCall = newCastingCall.callType === 'creator';
  const isGeneralCall = newCastingCall.callType === 'general';

  const briefLength = newCastingCall.brief.trim().length;
  const briefProgress = Math.min((briefLength / BRIEF_MIN_CHARS) * 100, 100);
  const briefValid = briefLength >= BRIEF_MIN_CHARS;
  const briefTooLong = briefLength > BRIEF_MAX_CHARS;

  const canProceedFromStep = useCallback((step) => {
    switch (step) {
      case 0:
        return true;
      case 1:
        if (mode === 'bubble' || mode === 'chat') return true;
        if (isGeneralCall && newCastingCall.scopeType === 'bubble') {
          return !!newCastingCall.bubbleId;
        }
        return true;
      case 2:
        if (isCreatorCall) {
          return newCastingCall.platforms.length > 0 && newCastingCall.promotionTypes.length > 0;
        } else {
          return newCastingCall.workTypes.length > 0;
        }
      case 3:
        return briefValid && !briefTooLong && newCastingCall.budget > 0 && hasAnyFunds;
      case 4:
        return true;
      case 5:
        return true;
      default:
        return true;
    }
  }, [mode, isGeneralCall, isCreatorCall, newCastingCall, briefValid, briefTooLong, hasAnyFunds]);

  const handleNext = useCallback(() => {
    if (canProceedFromStep(activeStep)) {
      setActiveStep((prev) => prev + 1);
    }
  }, [activeStep, canProceedFromStep]);

  const handleBack = useCallback(() => {
    setActiveStep((prev) => prev - 1);
  }, []);

  const getSteps = useCallback(() => {
    const baseSteps = [
      'Choose Type',
      'Set Scope',
      isCreatorCall ? 'Platform & Content' : 'Work Type',
      'Project Details',
      'Branding & Extras'
    ];

    if (isCreatorCall) {
      baseSteps.push('Invite Creators');
    }

    return baseSteps;
  }, [isCreatorCall]);

  const steps = useMemo(() => getSteps(), [getSteps]);
  const progress = ((activeStep + 1) / steps.length) * 100;

  const renderContextIndicator = useCallback(() => {
    if (mode === 'standalone') return null;

    return (
      <Alert 
        severity="info" 
        icon={mode === 'bubble' ? <BubbleIcon /> : <CampaignIcon />}
        sx={{ mb: 2 }}
      >
        <Typography variant="body2" fontWeight={600}>
          {mode === 'bubble' && `Creating casting call for: ${bubbleName || 'this bubble'}`}
          {mode === 'chat' && `Creating casting call in bubble chat: ${bubbleName || 'this conversation'}`}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          This casting call will be automatically scoped to this bubble
        </Typography>
      </Alert>
    );
  }, [mode, bubbleName]);

  const renderBudgetAvailabilityAlert = useCallback(() => {
    if (isLoadingFunds) {
      return (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CircularProgress size={16} />
            <Typography variant="body2">
              Checking your available funds...
            </Typography>
          </Stack>
        </Alert>
      );
    }

    if (!hasAnyFunds) {
      return (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }}
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={() => setShowAddBalanceDialog(true)}
              startIcon={<AddCircleIcon />}
            >
              Add Balance
            </Button>
          }
        >
          <AlertTitle>No Funds Available</AlertTitle>
          <Typography variant="body2">
            You need to add balance to create a casting call. {hasBlitzPayAccount && 'Your BlitzPay spending power is also at $0.'}
          </Typography>
        </Alert>
      );
    }

    const currentBalance = parseFloat(balanceData?.balance || 0);
    const spendingAvailable = hasBlitzPayAccount ? parseFloat(spendingInfo?.total_available_funds || 0) : 0;

    return (
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: alpha(theme.palette.success.main, 0.05),
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar
            sx={{
              bgcolor: alpha(theme.palette.success.main, 0.15),
              color: 'success.main',
            }}
          >
            <WalletIcon />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              Available Funds
            </Typography>
            <Typography variant="h5" fontWeight={700} color="success.main" sx={{ mb: 0.5 }}>
              ${maxAffordableBudget.toLocaleString()}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip 
                label={`Balance: $${currentBalance.toLocaleString()}`}
                size="small"
                variant="outlined"
                color="success"
              />
              {hasBlitzPayAccount && spendingAvailable > 0 && (
                <Chip
                  label={`Spending Power: $${spendingAvailable.toLocaleString()}`}
                  size="small"
                  variant="outlined"
                  color="primary"
                />
              )}
            </Stack>
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddCircleIcon />}
            onClick={() => setShowAddBalanceDialog(true)}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Add More
          </Button>
        </Stack>
      </Paper>
    );
  }, [isLoadingFunds, hasAnyFunds, hasBlitzPayAccount, balanceData, spendingInfo, maxAffordableBudget, theme.palette.success.main]);

  // Memoize form content props
  const formContentProps = useMemo(() => ({
    renderContextIndicator,
    activeStep,
    newCastingCall,
    handleCallTypeChange,
    isMobile,
    theme,
    mode,
    handleScopeChange,
    isGeneralCall,
    bubbleThemeColors,
    bubbleName,
    bubbles,
    bubblesLoading,
    isCreatorCall,
    handlePlatformChange,
    platforms,
    handlePromotionTypeChange,
    availablePromotionTypes,
    getPromoDisabledState,
    handleWorkTypeChange,
    renderBudgetAvailabilityAlert,
    handleInputChange,
    hasAnyFunds,
    briefLength,
    briefValid,
    briefTooLong,
    briefProgress,
    BRIEF_MAX_CHARS,
    BRIEF_MIN_CHARS,
    handleBudgetChange,
    maxAffordableBudget,
    isLoadingFunds,
    handleThemeSelect,
    handleThemeColorChange,
    handleOpenAssetDialog,
    handleRemoveSupportingImage,
    deliverableInput,
    setDeliverableInput,
    handleAddDeliverable,
    handleRemoveDeliverable,
    requirementInput,
    setRequirementInput,
    handleAddRequirement,
    handleRemoveRequirement,
    handlePickCreator,
    selectedCreators,
    setNewCastingCall,
    steps
  }), [
    renderContextIndicator,
    activeStep,
    newCastingCall,
    handleCallTypeChange,
    isMobile,
    theme,
    mode,
    handleScopeChange,
    isGeneralCall,
    bubbleThemeColors,
    bubbleName,
    bubbles,
    bubblesLoading,
    isCreatorCall,
    handlePlatformChange,
    platforms,
    handlePromotionTypeChange,
    availablePromotionTypes,
    getPromoDisabledState,
    handleWorkTypeChange,
    renderBudgetAvailabilityAlert,
    handleInputChange,
    hasAnyFunds,
    briefLength,
    briefValid,
    briefTooLong,
    briefProgress,
    handleBudgetChange,
    maxAffordableBudget,
    isLoadingFunds,
    handleThemeSelect,
    handleThemeColorChange,
    handleOpenAssetDialog,
    handleRemoveSupportingImage,
    deliverableInput,
    handleAddDeliverable,
    handleRemoveDeliverable,
    requirementInput,
    handleAddRequirement,
    handleRemoveRequirement,
    handlePickCreator,
    selectedCreators,
    steps
  ]);

  if (!open) return null;

  // Mobile View
  if (isMobile) {
    return (
      <>
        <Dialog 
          open={open} 
          onClose={handleClose}
          maxWidth="md"
          fullWidth
          fullScreen
          PaperProps={{
            elevation: 0,
            sx: { 
              borderRadius: 0,
              boxShadow: 'none',
              m: 0,
              paddingTop: 'env(safe-area-inset-top, 0px)',
              paddingBottom: 'env(safe-area-inset-bottom, 0px)',
              height: '100vh',
              maxHeight: '100vh',
            }
          }}
        >
          <Box 
            sx={{ 
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              color: 'white',
              position: 'sticky',
              top: 0,
              zIndex: 10,
              boxShadow: theme.shadows[4],
              pt: 'max(16px, env(safe-area-inset-top, 0px))',
            }}
          >
            <Box 
              sx={{ 
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `radial-gradient(circle at 20% 80%, ${alpha(theme.palette.secondary.main, 0.3)} 0%, transparent 70%)`,
                pointerEvents: 'none'
              }}
            />
            
            <Box sx={{ position: 'relative', zIndex: 1, p: 2, pb: 2 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="h6" fontWeight={800} sx={{ mb: 0.5 }}>
                    Create Casting Call
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      Step {activeStep + 1} of {steps.length} • {steps[activeStep]}
                    </Typography>
                    {(mode === 'bubble' || mode === 'chat') && (
                      <Chip 
                        label={mode === 'bubble' ? 'Bubble Mode' : 'Chat Mode'}
                        size="small"
                        icon={mode === 'bubble' ? <BubbleIcon /> : <CampaignIcon />}
                        sx={{ 
                          bgcolor: 'rgba(255,255,255,0.2)',
                          color: 'white',
                          height: 18,
                          fontSize: '0.65rem',
                          '& .MuiChip-icon': { color: 'white' }
                        }}
                      />
                    )}
                  </Stack>
                </Box>
                
                <IconButton 
                  onClick={handleClose}
                  size="small"
                  sx={{ 
                    color: 'white',
                    bgcolor: 'rgba(255,255,255,0.1)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Stack>

              <LinearProgress 
                variant="determinate" 
                value={progress} 
                sx={{
                  height: 4,
                  borderRadius: 3,
                  bgcolor: 'rgba(255,255,255,0.2)',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: 'white',
                    borderRadius: 3,
                  }
                }}
              />
            </Box>
          </Box>

          <DialogContent 
            sx={{ 
              p: 2,
              bgcolor: 'background.default',
              overflowY: 'auto',
              flex: 1,
              WebkitOverflowScrolling: 'touch',
              paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
            }}
          >
            <CallFormContent {...formContentProps} />
          </DialogContent>

          <Divider />

          <DialogActions 
            sx={{ 
              p: 2, 
              gap: 1, 
              bgcolor: 'background.paper',
              paddingBottom: 'calc(98px + 16px + env(safe-area-inset-bottom, 0px))',
            }}
          >
            <Button 
              onClick={handleClose} 
              variant="text"
              size="medium"
              sx={{ borderRadius: 2, fontSize: '0.875rem' }}
            >
              Cancel
            </Button>
            
            <Box sx={{ flex: 1 }} />
            
            {activeStep > 0 && (
              <Button 
                onClick={handleBack}
                variant="outlined"
                size="medium"
                startIcon={<ArrowBackIcon />}
                sx={{ borderRadius: 2, fontSize: '0.875rem' }}
              >
                Back
              </Button>
            )}
            
            {activeStep < steps.length - 1 ? (
              <Button 
                onClick={handleNext}
                variant="contained"
                size="medium"
                disabled={!canProceedFromStep(activeStep)}
                endIcon={<ArrowForwardIcon />}
                sx={{ borderRadius: 2, minWidth: 90, fontSize: '0.875rem' }}
              >
                Next
              </Button>
            ) : (
              <Button 
                onClick={handleSubmit}
                variant="contained"
                size="medium"
                disabled={isPosting || !canProceedFromStep(activeStep) || !hasAnyFunds}
                sx={{ borderRadius: 2, minWidth: 90, fontSize: '0.875rem' }}
              >
                {isPosting ? <CircularProgress size={18} color="inherit" /> : "Create"}
              </Button>
            )}
          </DialogActions>
        </Dialog>

        <AddBalanceDialog
          open={showAddBalanceDialog}
          onClose={() => setShowAddBalanceDialog(false)}
          onSuccess={handleAddBalanceSuccess}
        />

        <SelectAssetDialog
          open={assetDialogOpen}
          onClose={handleAssetDialogClose}
          selectedAsset={selectedAsset}
          onAssetClick={handleAssetClick}
        />
      </>
    );
  }

  // Desktop View - Full Screen Overlay
  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        fullScreen
        PaperProps={{
          sx: {
            m: 0,
            maxHeight: '100vh',
            maxWidth: '100vw',
            borderRadius: 0,
          }
        }}
        disablePortal={false}
        keepMounted={false}
        sx={{
          '& .MuiDialog-container': {
            alignItems: 'stretch',
          }
        }}
      >
        {/* Header Bar */}
        <Box
          sx={{
            height: 80,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 4,
            bgcolor: 'background.paper',
            backdropFilter: 'blur(20px)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2.5,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
              }}
            >
              <CampaignIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                Create Casting Call
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Step {activeStep + 1} of {steps.length} • {steps[activeStep]}
              </Typography>
            </Box>
          </Box>

          <Stack direction="row" spacing={2} alignItems="center">
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                width: 200,
                height: 8,
                borderRadius: 4,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
              }}
            />
            <IconButton 
              onClick={handleClose}
              sx={{
                bgcolor: 'action.hover',
                '&:hover': { bgcolor: 'action.selected' },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>

        {/* Main Content - Two Column Layout */}
        <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {/* Left Column - Preview */}
          <Box
            sx={{
              width: 380,
              borderRight: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              bgcolor: 'background.paper',
              overflowY: 'auto',
              p: 3,
            }}
          >
            <PreviewCard 
              newCastingCall={newCastingCall}
              maxAffordableBudget={maxAffordableBudget}
              isCreatorCall={isCreatorCall}
              briefLength={briefLength}
              briefProgress={briefProgress}
              briefValid={briefValid}
              briefTooLong={briefTooLong}
              selectedCreators={selectedCreators}
              bubbleName={bubbleName}
              activeStep={activeStep}
              totalSteps={steps.length}
              BRIEF_MAX_CHARS={BRIEF_MAX_CHARS}
            />
          </Box>

          {/* Right Column - Form */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: 4 }}>
            <CallFormContent {...formContentProps} />

            {/* Action Buttons - Sticky at bottom */}
            <Box 
              sx={{ 
                position: 'sticky',
                bottom: 0,
                bgcolor: 'background.default',
                pt: 3,
                pb: 2,
                borderTop: '1px solid',
                borderColor: 'divider',
                mt: 4,
              }}
            >
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button 
                  onClick={handleClose} 
                  variant="text"
                  size="large"
                  sx={{ borderRadius: 2, px: 3 }}
                >
                  Cancel
                </Button>
                
                {activeStep > 0 && (
                  <Button 
                    onClick={handleBack}
                    variant="outlined"
                    size="large"
                    startIcon={<ArrowBackIcon />}
                    sx={{ borderRadius: 2, px: 3 }}
                  >
                    Back
                  </Button>
                )}
                
                {activeStep < steps.length - 1 ? (
                  <Button 
                    onClick={handleNext}
                    variant="contained"
                    size="large"
                    disabled={!canProceedFromStep(activeStep)}
                    endIcon={<ArrowForwardIcon />}
                    sx={{ borderRadius: 2, px: 4 }}
                  >
                    Next
                  </Button>
                ) : (
                  <Button 
                    onClick={handleSubmit}
                    variant="contained"
                    size="large"
                    disabled={isPosting || !canProceedFromStep(activeStep) || !hasAnyFunds}
                    sx={{ borderRadius: 2, px: 4 }}
                  >
                    {isPosting ? <CircularProgress size={20} color="inherit" /> : "Create Casting Call"}
                  </Button>
                )}
              </Stack>
            </Box>
          </Box>
        </Box>
      </Dialog>

      <AddBalanceDialog
        open={showAddBalanceDialog}
        onClose={() => setShowAddBalanceDialog(false)}
        onSuccess={handleAddBalanceSuccess}
      />

      <SelectAssetDialog
        open={assetDialogOpen}
        onClose={handleAssetDialogClose}
        selectedAsset={selectedAsset}
        onAssetClick={handleAssetClick}
      />
    </>
  );
}